import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { trackEvent } from '../analytics/posthog';

const ActivityContext = createContext();

export const useActivity = () => useContext(ActivityContext);

export const ActivityProvider = ({ children }) => {
    const [activities, setActivities] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [loading, setLoading] = useState(false);

    // Fetch activities from backend
    const fetchActivities = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.getActivities(100);
            const data = Array.isArray(response.data) ? response.data : [];
            const backendActivities = data.map(log => ({
                id: log.log_id,
                user: log.user_id,
                action: formatAction(log.action, log.details, log.resource_type),
                target: log.details?.name || log.resource_id,
                type: mapResourceType(log.resource_type),
                time: new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                timestamp: log.timestamp,
                resourceType: log.resource_type,
                resourceId: log.resource_id,
                projectId: log.project_id,
                details: log.details
            }));
            setActivities(backendActivities);
        } catch (error) {
            console.error('Failed to fetch activities:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch and refresh on trigger
    useEffect(() => {
        fetchActivities();
    }, [fetchActivities, refreshTrigger]);

    // Add a local activity (for immediate UI feedback before backend refresh)
    const addActivity = (activity) => {
        const newActivity = {
            id: Date.now(),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            ...activity
        };
        setActivities(prev => [newActivity, ...prev]);
        // Mirror to PostHog analytics
        trackEvent('user_activity', {
            action: activity.action,
            target: activity.target,
            type: activity.type
        });
    };

    const triggerRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    return (
        <ActivityContext.Provider value={{
            activities,
            addActivity,
            refreshTrigger,
            triggerRefresh,
            fetchActivities,
            loading
        }}>
            {children}
        </ActivityContext.Provider>
    );
};

/**
 * Build a precise, human-readable description from the action + details JSON.
 *
 * The details field already contains rich context from each log_activity() call:
 *   - name, type, version, change_description, message
 *   - source_branch, target_branch, files_changed
 *   - permission_level, target_user_id
 *   - field_updates, etc.
 */
function formatAction(action, details, resourceType) {
    const d = details || {};
    const name = d.name || '';

    switch (action) {
        // ── Documents ──
        case 'create_document':
            return name
                ? `Created document ${name}` + (d.type ? ` (${d.type})` : '')
                : 'Created document';

        case 'update_document':
            if (d.field_updates && typeof d.field_updates === 'object') {
                const fields = Object.keys(d.field_updates).join(', ');
                return name ? `Updated ${fields} on ${name}` : `Updated document (${fields})`;
            }
            return name ? `Updated document ${name}` : 'Updated document';

        case 'delete_document':
            return name ? `Deleted document ${name}` : 'Deleted document';

        case 'upload_version':
            return name
                ? `Uploaded version ${d.version || ''} of ${name}`.trim()
                    + (d.change_description ? ` \u2014 "${d.change_description}"` : '')
                : `Uploaded version ${d.version || ''}`.trim()
                    + (d.change_description ? ` \u2014 "${d.change_description}"` : '');

        // ── Parts ──
        case 'create_part':
            return name
                ? `Created part ${name}` + (d.type ? ` (${d.type})` : '')
                    + (d.parent_assembly ? ` under ${d.parent_assembly}` : '')
                : 'Created part';

        case 'update_part':
            if (d.field_updates && typeof d.field_updates === 'object') {
                const fields = Object.keys(d.field_updates).join(', ');
                return name ? `Updated ${fields} on part ${name}` : `Updated part (${fields})`;
            }
            return name ? `Updated part ${name}` : 'Updated part';

        case 'delete_part':
            return name ? `Deleted part ${name}` : 'Deleted part';

        // ── Requirements ──
        case 'create_requirement':
            return d.description
                ? `Created requirement \u2014 "${d.description.slice(0, 80)}${d.description.length > 80 ? '...' : ''}"`
                    + (d.has_attachment ? ' (with attachment)' : '')
                : 'Created requirement';

        case 'update_requirement':
            if (d.field_updates && typeof d.field_updates === 'object') {
                const fields = Object.keys(d.field_updates).join(', ');
                return `Updated requirement (${fields})`;
            }
            return 'Updated requirement';

        case 'delete_requirement':
            return 'Deleted requirement';

        // ── Projects ──
        case 'create_project':
            return name ? `Created project ${name}` : 'Created project';

        case 'update_project':
            if (d.field_updates && typeof d.field_updates === 'object') {
                const fields = Object.keys(d.field_updates).join(', ');
                return name ? `Updated ${fields} on project ${name}` : `Updated project (${fields})`;
            }
            return name ? `Updated project ${name}` : 'Updated project';

        case 'delete_project':
            return name ? `Deleted project ${name}` : 'Deleted project';

        // ── Branches ──
        case 'create_branch':
            return name
                ? `Created branch ${name}`
                    + (d.source ? ` from ${d.source}` : '')
                    + (d.inherited_files_count ? ` (${d.inherited_files_count} files)` : '')
                : 'Created branch';

        case 'delete_branch':
            return name ? `Deleted branch ${name}` : 'Deleted branch';

        // ── Commits ──
        case 'commit': {
            const branch = d.branch_id || '';
            const msg = d.message || '';
            const count = d.files_changed;
            let text = 'Committed';
            if (count) text += ` ${count} file${count > 1 ? 's' : ''}`;
            if (branch) text += ` to ${branch}`;
            if (msg) text += ` \u2014 "${msg}"`;
            if (d.is_merge_commit) text += ' (merge)';
            return text;
        }

        // ── Merges ──
        case 'create_merge_request':
            return d.title
                ? `Created merge request "${d.title}"`
                    + (d.has_conflicts ? ' (has conflicts)' : '')
                : 'Created merge request';

        case 'merge_branch': {
            let text = 'Merged branch';
            if (d.eco_id) text += ` (${d.eco_id})`;
            if (d.target_branch) text += ` into ${d.target_branch}`;
            if (d.files_updated) text += ` \u2014 ${d.files_updated} files`;
            if (d.approved_by) text += `, approved by ${d.approved_by}`;
            return text;
        }

        // ── Simulations ──
        case 'create_simulation':
            return name
                ? `Created simulation ${name}` + (d.type ? ` (${d.type})` : '')
                : 'Created simulation';

        case 'run_simulation':
            return name ? `Ran simulation ${name}` : 'Ran simulation';

        // ── Access Control ──
        case 'grant_permission':
            return d.target_user_id
                ? `Granted ${d.permission_level || 'access'} to ${d.target_user_id}`
                : 'Granted permission';

        case 'revoke_permission':
            return d.target_user_id
                ? `Revoked access for ${d.target_user_id}`
                : 'Revoked permission';

        case 'permission_approved':
            return d.target_user_id
                ? `Approved ${d.permission_level || ''} access for ${d.target_user_id}`.trim()
                : 'Approved permission request';

        case 'permission_rejected':
            return d.target_user_id
                ? `Rejected access request from ${d.target_user_id}`
                : 'Rejected permission request';

        // ── Version Control ──
        case 'stage_file':
            return name ? `Staged ${name}` : 'Staged file';

        case 'unstage_file':
            return name ? `Unstaged ${name}` : 'Unstaged file';

        case 'create_tag':
            return name ? `Created tag ${name}` : 'Created tag';

        case 'delete_tag':
            return name ? `Deleted tag ${name}` : 'Deleted tag';

        case 'create_release':
            return name ? `Created release ${name}` : 'Created release';

        case 'publish_release':
            return name ? `Published release ${name}` : 'Published release';

        case 'rebase':
            return 'Rebased branch';

        case 'cherry_pick':
            return 'Cherry-picked commit';

        case 'reset_branch':
            return 'Reset branch';

        case 'create_experiment':
            return name ? `Created experiment ${name}` : 'Created experiment';

        // ── Compliance ──
        case 'create_compliance_standard':
            return d.description
                ? `Created compliance standard \u2014 "${d.description.slice(0, 80)}${d.description.length > 80 ? '...' : ''}"`
                    + (d.has_attachment ? ' (with attachment)' : '')
                : 'Created compliance standard';

        case 'update_compliance_standard':
            if (d.changes && typeof d.changes === 'object') {
                const fields = Object.keys(d.changes).join(', ');
                return `Updated compliance standard (${fields})`;
            }
            return 'Updated compliance standard';

        case 'delete_compliance_standard':
            return d.description
                ? `Deleted compliance standard \u2014 "${d.description.slice(0, 60)}"`
                : 'Deleted compliance standard';

        case 'run_compliance_check':
            return name
                ? `Ran compliance check on ${name}`
                    + (d.pass_count != null ? ` \u2014 ${d.pass_count}P/${d.fail_count || 0}F/${d.warning_count || 0}W` : '')
                : 'Ran compliance check';

        case 'override_compliance_verdict':
            return name
                ? `Manual compliance override on ${name} \u2014 ${d.status || 'cleared'}`
                : `Manual compliance override \u2014 ${d.status || 'cleared'}`;

        case 'export_traceability_matrix':
            return 'Exported traceability matrix';

        case 'export_compliance_result':
            return name ? `Exported compliance results for ${name}` : 'Exported compliance results';

        // ── Fallback ──
        default:
            // Convert snake_case to readable text and append name if available
            return (action.replace(/_/g, ' ') + (name ? ` \u2014 ${name}` : '')).trim();
    }
}

// Helper to map resource types to activity types
function mapResourceType(resourceType) {
    const typeMap = {
        'document': 'edit',
        'requirement': 'edit',
        'part': 'edit',
        'project': 'edit',
        'branch': 'merge',
        'simulation': 'sim',
        'eco': 'eco',
        'compliance_standard': 'compliance',
        'compliance_check': 'compliance',
    };
    return typeMap[resourceType] || 'edit';
}
